import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';

import PropTypes from 'prop-types';

import { refreshAccessToken, registerSessionHandlers } from '../../api';
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
  subscribeAccessToken,
} from '../../utils/accessTokenStore';
import { disconnectConsumer } from '../../utils/actionCable';
import { decodeJWT } from '../../utils/jwt';
import AuthContext from './AuthContext';

// Refresh the access token before it expires to accommodate slow request / response
// times.
const REFRESH_BUFFER_IN_SECONDS = 60;

const AuthProvider = ({ children, entityType }) => {
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);

  const accessToken = useSyncExternalStore(subscribeAccessToken, getAccessToken);

  const clearSession = useCallback(() => {
    clearAccessToken();
    setEntity(null);
    disconnectConsumer();
    // TODO: Logout of Stripe Connect when :entityType is User.
    // if (entityType === 'user') stripeConnectInstance.logout();
  }, []);

  const startSession = useCallback(({ jwtToken, entity: nextEntity }) => {
    setAccessToken(jwtToken);
    setEntity(nextEntity);
  }, []);

  useEffect(() => {
    registerSessionHandlers({
      onExpired: clearSession,
      onRefreshed: ({ entity: nextEntity }) => setEntity(nextEntity),
    });
  }, [clearSession]);

  useEffect(() => {
    let active = true;

    refreshAccessToken()
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!accessToken) return undefined;

    let expiration;

    try {
      ({ exp: expiration } = decodeJWT());
    } catch (error) {
      return undefined;
    }

    if (!expiration) return undefined;

    const expiresAtMs = expiration * 1000; // JWT exp is in seconds; convert to ms.
    const msUntilExpiry = expiresAtMs - Date.now();
    const bufferMs = REFRESH_BUFFER_IN_SECONDS * 1000;
    const delay = Math.max(msUntilExpiry - bufferMs, 0);

    const timer = setTimeout(() => {
      refreshAccessToken().catch(() => {});
    }, delay);

    return () => clearTimeout(timer);
  }, [accessToken]);

  const contextValue = useMemo(
    () => ({
      accessToken,
      clearSession,
      loading,
      startSession,
      [entityType]: entity,
    }),
    [accessToken, clearSession, entity, entityType, loading, startSession]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
  entityType: PropTypes.oneOf(['client', 'user']).isRequired,
};

export default AuthProvider;
